import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

import { opportunitiesService } from '../../services/partnerService';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

const STATUS_OPTIONS = ['Identified', 'Qualified', 'Proposal', 'Negotiation', 'Won', 'Lost'];
const SECTOR_OPTIONS = ['Healthcare', 'Financial Services', 'Technology', 'Retail & E-Commerce', 'Manufacturing', 'Energy & Utilities', 'Telecommunications', 'Education', 'Government & Public Sector', 'Real Estate', 'Transportation & Logistics', 'Media & Entertainment', 'Professional Services', 'Non-Profit', 'Other'];
const ROLE_OPTIONS = ['Lead', 'Co-Lead', 'Support', 'Technical Advisor', 'Commercial', 'Delivery', 'Bid Manager'];

const STATUS_COLORS = {
  identified: 'bg-slate-100 text-slate-600',
  qualified: 'bg-primary/10 text-primary',
  proposal: 'bg-warning/10 text-warning',
  negotiation: 'bg-accent/10 text-accent',
  won: 'bg-success/10 text-success',
  lost: 'bg-error/10 text-error',
};

const OpportunityProfilePage = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const [opportunity, setOpportunity] = useState(location?.state?.opportunity || null);
  const [loading, setLoading] = useState(!location?.state?.opportunity);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState({
    name: '',
    value: '',
    opp_status: 'Identified',
    win_probability: 50,
    expected_close: '',
    sector: '',
    role: '',
    description: '',
    owned_by: 'eCity',
  });

  useEffect(() => {
    if (opportunity) {
      setForm({
        name: opportunity?.name || '',
        value: opportunity?.rawValue || '',
        opp_status: opportunity?.status || 'Identified',
        win_probability: opportunity?.winProbability || 50,
        expected_close: opportunity?.rawExpectedClose || '',
        sector: opportunity?.sector || '',
        role: opportunity?.role || '',
        description: opportunity?.description || '',
        owned_by: opportunity?.ownedBy || 'eCity',
      });
    }
  }, [opportunity]);

  useEffect(() => {
    if (!opportunity && id) {
      const fetchOpportunity = async () => {
        setLoading(true);
        try {
          const { data, error } = await supabase
            ?.from('opportunities')
            ?.select('*, partners(brand_name, legal_name)')
            ?.eq('id', id)
            ?.single();
          if (error) throw error;
          const o = data;
          setOpportunity({
            id: o?.id,
            name: o?.name,
            sector: o?.sector,
            value: o?.value >= 1000000 ? `OMR ${(o?.value / 1000000)?.toFixed(1)}M` : `OMR ${(o?.value / 1000)?.toFixed(0)}K`,
            rawValue: o?.value,
            role: o?.role,
            status: o?.opp_status,
            winProbability: o?.win_probability,
            expectedClose: o?.expected_close ? new Date(o.expected_close)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
            rawExpectedClose: o?.expected_close || '',
            ownedBy: o?.owned_by || 'eCity',
            partner_id: o?.partner_id || '',
            partnerName: o?.partners?.brand_name || o?.partners?.legal_name || '',
            description: o?.description || '',
          });
        } catch (err) {
          toast?.error('Failed to load opportunity');
        } finally {
          setLoading(false);
        }
      };
      fetchOpportunity();
    }
  }, [id]);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const handleSave = async () => {
    if (!form?.name?.trim()) { toast?.error('Opportunity title is required.'); return; }
    setSaving(true);
    try {
      await opportunitiesService?.update(id, {
        name: form?.name,
        value: parseFloat(form?.value) || 0,
        opp_status: form?.opp_status,
        win_probability: parseInt(form?.win_probability) || 0,
        expected_close: form?.expected_close || null,
        sector: form?.sector,
        role: form?.role,
        description: form?.description,
        owned_by: form?.owned_by,
      });
      toast?.success('Opportunity updated successfully');
      setOpportunity(prev => ({
        ...prev,
        name: form?.name,
        status: form?.opp_status,
        sector: form?.sector,
        role: form?.role,
        winProbability: parseInt(form?.win_probability) || 0,
        rawExpectedClose: form?.expected_close,
        expectedClose: form?.expected_close ? new Date(form.expected_close)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'TBD',
        description: form?.description,
        ownedBy: form?.owned_by,
        rawValue: parseFloat(form?.value) || 0,
        value: parseFloat(form?.value) >= 1000000 ? `OMR ${(parseFloat(form?.value) / 1000000)?.toFixed(1)}M` : `OMR ${(parseFloat(form?.value) / 1000)?.toFixed(0)}K`,
      }));
      setIsEditing(false);
    } catch (err) {
      toast?.error(err?.message || 'Failed to update opportunity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const { error } = await supabase?.from('opportunities')?.delete()?.eq('id', id);
      if (error) throw error;
      toast?.success('Opportunity deleted');
      navigate('/engagement-management-hub');
    } catch (err) {
      toast?.error(err?.message || 'Failed to delete opportunity');
      setDeleting(false);
    }
  };

  const statusKey = opportunity?.status?.toLowerCase();
  const statusClass = STATUS_COLORS?.[statusKey] || 'bg-slate-100 text-slate-600';
  const ownershipBadge = opportunity?.ownedBy === 'Partner'
    ? { label: '🤝 Partner-Led', className: 'bg-purple-100 text-purple-700 border border-purple-200' }
    : { label: '🏢 eCity-Led', className: 'bg-blue-100 text-blue-700 border border-blue-200' };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-blue-50/20">
        <Header />
        <div className="flex items-center justify-center py-64">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{opportunity?.name || 'Opportunity'} — PartnerHub</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50/30 to-blue-50/20">
        <Header />
        <main className="px-24 lg:px-32 pt-24 pb-48">
          <div className="max-w-4xl mx-auto">
            {/* Back */}
            <button
              onClick={() => navigate('/engagement-management-hub')}
              className="flex items-center gap-8 text-sm text-slate-500 hover:text-slate-800 mb-24 transition-all"
            >
              <Icon name="ArrowLeft" size={16} />
              Back to Engagement Hub
            </button>

            {/* Header Card */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-28 mb-20">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-16 mb-20">
                <div className="flex items-center gap-16">
                  <div className="p-14 bg-gradient-to-br from-secondary to-primary rounded-xl shadow-md">
                    <Icon name="Target" size={28} className="text-white" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">{opportunity?.name}</h1>
                    <p className="text-sm text-slate-500 mt-4">{opportunity?.partnerName && `Partner: ${opportunity?.partnerName}`}</p>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  {!isEditing && (
                    <>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center gap-6 px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-amber-50 hover:border-amber-200 hover:text-amber-700 transition-all"
                      >
                        <Icon name="Pencil" size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-6 px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-all"
                      >
                        <Icon name="Trash2" size={14} />
                        Delete
                      </button>
                    </>
                  )}
                  {isEditing && (
                    <>
                      <button
                        onClick={() => setIsEditing(false)}
                        className="px-14 py-8 text-sm font-medium border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-6 px-14 py-8 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 transition-all"
                      >
                        {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Icon name="Check" size={14} />}
                        Save Changes
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-8">
                <span className={`inline-flex items-center px-10 py-5 rounded-full text-xs font-semibold ${statusClass}`}>
                  {opportunity?.status}
                </span>
                <span className={`inline-flex items-center px-10 py-5 rounded-full text-xs font-semibold ${ownershipBadge?.className}`}>
                  {ownershipBadge?.label}
                </span>
                {opportunity?.role && (
                  <span className="inline-flex items-center px-10 py-5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                    {opportunity?.role}
                  </span>
                )}
              </div>
            </div>

            {/* Delete Confirm */}
            {showDeleteConfirm && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-24 mb-20 flex flex-col sm:flex-row sm:items-center gap-16">
                <div className="flex items-center gap-12 flex-1">
                  <div className="p-10 bg-red-100 rounded-full">
                    <Icon name="AlertTriangle" size={20} className="text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-red-800">Delete this opportunity?</p>
                    <p className="text-xs text-red-600 mt-2">This action cannot be undone.</p>
                  </div>
                </div>
                <div className="flex gap-8">
                  <button onClick={() => setShowDeleteConfirm(false)} className="px-16 py-8 text-sm border border-red-200 rounded-lg text-red-700 hover:bg-red-100 transition-all">Cancel</button>
                  <button onClick={handleDelete} disabled={deleting} className="px-16 py-8 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 transition-all">
                    {deleting ? 'Deleting...' : 'Confirm Delete'}
                  </button>
                </div>
              </div>
            )}

            {/* Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-20">
              {/* Main Info */}
              <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                <h2 className="text-base font-semibold text-slate-800 mb-20 flex items-center gap-8">
                  <Icon name="Info" size={16} className="text-primary" />
                  Opportunity Details
                </h2>
                {isEditing ? (
                  <div className="space-y-16">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Title <span className="text-error">*</span></label>
                      <input name="name" value={form?.name} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Status</label>
                        <select name="opp_status" value={form?.opp_status} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                          {STATUS_OPTIONS?.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Sector</label>
                        <select name="sector" value={form?.sector} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                          <option value="">Select sector</option>
                          {SECTOR_OPTIONS?.map(s => <option key={s}>{s}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Value (OMR)</label>
                        <input name="value" type="number" value={form?.value} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Win Probability (%)</label>
                        <input name="win_probability" type="number" min="0" max="100" value={form?.win_probability} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-12">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Engagement Role</label>
                        <select name="role" value={form?.role} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30">
                          <option value="">Select role</option>
                          {ROLE_OPTIONS?.map(r => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-6">Expected Close</label>
                        <input name="expected_close" type="date" value={form?.expected_close} onChange={handleChange} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Owned By</label>
                      <div className="flex gap-8">
                        {['eCity', 'Partner']?.map(opt => (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setForm(prev => ({ ...prev, owned_by: opt }))}
                            className={`flex-1 py-10 text-sm font-medium rounded-lg border transition-all ${form?.owned_by === opt ? (opt === 'eCity' ? 'bg-blue-600 text-white border-blue-600' : 'bg-purple-600 text-white border-purple-600') : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                          >
                            {opt === 'eCity' ? '🏢 eCity' : '🤝 Partner'}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-6">Description</label>
                      <textarea name="description" value={form?.description} onChange={handleChange} rows={4} className="w-full px-12 py-10 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-16">
                    <div className="grid grid-cols-2 gap-16">
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Sector</p>
                        <p className="text-sm font-medium text-slate-800">{opportunity?.sector || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Engagement Role</p>
                        <p className="text-sm font-medium text-slate-800">{opportunity?.role || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Deal Value</p>
                        <p className="text-sm font-semibold text-slate-800">{opportunity?.value || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-4">Expected Close</p>
                        <p className="text-sm font-medium text-slate-800">{opportunity?.expectedClose || '—'}</p>
                      </div>
                    </div>
                    {opportunity?.description && (
                      <div className="pt-16 border-t border-slate-100">
                        <p className="text-xs text-slate-500 mb-8">Description</p>
                        <p className="text-sm text-slate-700 leading-relaxed">{opportunity?.description}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Win Probability */}
              <div className="space-y-16">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                  <h2 className="text-base font-semibold text-slate-800 mb-16 flex items-center gap-8">
                    <Icon name="TrendingUp" size={16} className="text-success" />
                    Win Probability
                  </h2>
                  <div className="flex items-end gap-8 mb-12">
                    <span className="text-4xl font-bold text-slate-900">{opportunity?.winProbability}</span>
                    <span className="text-lg text-slate-500 mb-4">%</span>
                  </div>
                  <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all"
                      style={{ width: `${opportunity?.winProbability}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-8">
                    {opportunity?.winProbability >= 70 ? 'High likelihood' : opportunity?.winProbability >= 40 ? 'Moderate likelihood' : 'Low likelihood'}
                  </p>
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-24">
                  <h2 className="text-base font-semibold text-slate-800 mb-16 flex items-center gap-8">
                    <Icon name="Activity" size={16} className="text-primary" />
                    Quick Stats
                  </h2>
                  <div className="space-y-12">
                    <div className="flex items-center justify-between py-8 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Status</span>
                      <span className={`text-xs font-semibold px-8 py-4 rounded-full ${statusClass}`}>{opportunity?.status}</span>
                    </div>
                    <div className="flex items-center justify-between py-8 border-b border-slate-50">
                      <span className="text-xs text-slate-500">Ownership</span>
                      <span className="text-xs font-medium text-slate-700">{opportunity?.ownedBy || 'eCity'}</span>
                    </div>
                    <div className="flex items-center justify-between py-8">
                      <span className="text-xs text-slate-500">Partner</span>
                      <span className="text-xs font-medium text-slate-700 truncate max-w-[120px]">{opportunity?.partnerName || '—'}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default OpportunityProfilePage;
